/**
 * needle-runtime-service.ts
 *
 * Dedicated public service boundary for Needle 2 Elastic Worker Pools.
 * Invariant: This service strictly operates on kind='needle2' runtimes and cannot
 * mutate or access LLM domain states.
 */

import { ModelRuntime } from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore, PoolRevision } from '../storage/orchestration-store.js';
import { RuntimeRegistry } from './runtime-registry.js';
import { NodeCapabilityRegistry } from '../nodes/node-capability-registry.js';
import { ModelDefinitionRegistry } from '../catalog/model-definition-registry.js';

export class NeedleRuntimeService {
  private readonly registry: RuntimeRegistry;

  constructor(
    store: OrchestrationStore,
    nodeRegistry: NodeCapabilityRegistry,
    modelRegistry: ModelDefinitionRegistry
  ) {
    this.registry = new RuntimeRegistry(store, nodeRegistry, modelRegistry);
  }

  async spawnInstance(nodeId: string, modelId: string = 'needle-2-q4km'): Promise<ModelRuntime> {
    return this.registry.spawnNeedleInstance(nodeId, modelId);
  }

  async scalePool(
    nodeId: string,
    modelId: string = 'needle-2-q4km',
    targetCount: number,
    poolRevision?: PoolRevision
  ): Promise<void> {
    return this.registry.scaleNeedlePool(nodeId, modelId, targetCount, poolRevision?.revision);
  }

  async stopWorker(runtimeId: string, mode: 'drain' | 'terminate' = 'drain'): Promise<void> {
    return this.registry.stopRuntime(runtimeId, mode);
  }

  async forceKillWorker(runtimeId: string): Promise<void> {
    return this.registry.stopRuntime(runtimeId, 'force_kill');
  }

  async getWorkers(nodeId: string): Promise<readonly ModelRuntime[]> {
    return this.registry.getRuntimesForNode(nodeId, 'needle2');
  }

  async getAllNeedleWorkers(): Promise<readonly ModelRuntime[]> {
    const all = await this.registry.getAllRuntimes();
    return all.filter((r) => r.kind === 'needle2');
  }
}
